import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const FormsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const prefix = isEnglish ? '/en' : '';

  const forms = [
    {
      key: 'homeInsurance',
      icon: (
        <svg className="w-12 h-12 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      title: isEnglish ? 'Home Insurance' : 'Seguro de Hogar',
      description: isEnglish
        ? 'Complete the form to get a quote for your home insurance'
        : 'Complete el formulario para obtener una cotización de seguro de hogar',
      link: isEnglish ? `${prefix}/wip/forms/home-insurance` : `${prefix}/wip/formularios/seguro-de-hogar`,
      available: true,
    },
    {
      key: 'autoInsurance',
      icon: (
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
      ),
      title: isEnglish ? 'Auto Insurance' : 'Seguro de Auto',
      description: isEnglish
        ? 'Coming soon - Auto insurance form'
        : 'Próximamente - Formulario de seguro de auto',
      link: '#',
      available: false,
    },
    {
      key: 'medicalInsurance',
      icon: (
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      ),
      title: isEnglish ? 'Major Medical' : 'Gastos Médicos Mayores',
      description: isEnglish
        ? 'Coming soon - Major medical insurance form'
        : 'Próximamente - Formulario de gastos médicos mayores',
      link: '#',
      available: false,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-700 to-primary-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {isEnglish ? 'Insurance Forms' : 'Formularios de Seguros'}
            </h1>
            <p className="text-xl text-primary-100 max-w-2xl mx-auto">
              {isEnglish
                ? 'Select a form to start your insurance application'
                : 'Seleccione un formulario para iniciar su solicitud de seguro'}
            </p>
          </div>
        </div>
      </section>

      {/* Forms Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {forms.map((form) => (
              <div
                key={form.key}
                className={`bg-white rounded-xl shadow-lg overflow-hidden ${
                  form.available
                    ? 'hover:shadow-xl transition-all duration-300'
                    : 'opacity-60'
                }`}
              >
                <div className="p-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    form.available ? 'bg-primary-100' : 'bg-gray-100'
                  }`}>
                    {form.icon}
                  </div>
                  <h3 className={`text-2xl font-bold mb-3 ${
                    form.available ? 'text-gray-900' : 'text-gray-500'
                  }`}>
                    {form.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {form.description}
                  </p>
                  {form.available ? (
                    <Link
                      to={form.link}
                      className="inline-flex items-center justify-center w-full bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                    >
                      {isEnglish ? 'Start Form' : 'Iniciar Formulario'}
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  ) : (
                    <button
                      disabled
                      className="inline-flex items-center justify-center w-full bg-gray-300 text-gray-500 px-6 py-3 rounded-lg font-semibold cursor-not-allowed"
                    >
                      {isEnglish ? 'Coming Soon' : 'Próximamente'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isEnglish ? 'Need Help?' : '¿Necesita Ayuda?'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isEnglish
              ? 'If you have questions about any of our forms, our team is here to help you.'
              : 'Si tiene preguntas sobre alguno de nuestros formularios, nuestro equipo está aquí para ayudarle.'}
          </p>
          <Link
            to={isEnglish ? `${prefix}/contact` : `${prefix}/contacto`}
            className="inline-block bg-primary-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            {isEnglish ? 'Contact Us' : 'Contáctenos'}
          </Link>
        </div>
      </section>
    </>
  );
};

export default FormsPage;
